// Mirror of the C# GarrisonKey helper in
// shared/ElementalRealms.Shared/Runtime/Combat/GarrisonKey.cs.
// Garrison / hospital-pool / temple-pool dictionaries are keyed by
// "<unitId>@<tier>" composite tokens. The bare unitId (no "@") is the
// legacy tier 1 form — pre-T1-T10 saves keep working without migration.

const SEP = "@";

export function buildGarrisonKey(unitId: string, tier = 1): string {
  if (!unitId) throw new Error("unitId required");
  if (tier <= 1) return unitId;
  return `${unitId}${SEP}${tier}`;
}

export function parseGarrisonKey(key: string): { unitId: string; tier: number } {
  if (!key) return { unitId: "", tier: 1 };
  const idx = key.indexOf(SEP);
  if (idx < 0) return { unitId: key, tier: 1 };
  const unitId = key.slice(0, idx);
  const tier = Number.parseInt(key.slice(idx + 1), 10);
  return { unitId, tier: Number.isFinite(tier) && tier >= 1 ? tier : 1 };
}

export function unitIdOf(key: string): string {
  const idx = key.indexOf(SEP);
  return idx < 0 ? key : key.slice(0, idx);
}

export function tierOf(key: string): number {
  const idx = key.indexOf(SEP);
  if (idx < 0) return 1;
  const tier = Number.parseInt(key.slice(idx + 1), 10);
  return Number.isFinite(tier) && tier >= 1 ? tier : 1;
}

// Mirror of UnitTierScaling on the C# side.
export const TIER_MIN = 1;
export const TIER_MAX = 10;
export const TIER_PER_LEVEL = 0.20; // stat curve
export const TIER_COST_PER_LEVEL = 0.20;
export const TIER_TIME_PER_LEVEL = 0.10;

// Max batch size for a single training order. Mirrors
// `CastleService.MaxBatchForLevel` on the server — 350 units per
// training-building level. The server rejects oversized batches with
// `BatchTooLarge`; the UI honors the same cap so the «Макс» button and
// presets don't suggest a value the server will refuse.
export const MAX_BATCH_PER_LEVEL = 350;

export function maxBatchForBuildingLevel(buildingLevel: number): number {
  return buildingLevel <= 0 ? 0 : buildingLevel * MAX_BATCH_PER_LEVEL;
}

export function tierStatMultiplier(tier: number): number {
  const t = Math.max(TIER_MIN, Math.min(TIER_MAX, tier));
  return 1.0 + TIER_PER_LEVEL * (t - 1);
}

export function tierCostMultiplier(tier: number): number {
  const t = Math.max(TIER_MIN, Math.min(TIER_MAX, tier));
  return 1.0 + TIER_COST_PER_LEVEL * (t - 1);
}

export function tierTimeMultiplier(tier: number): number {
  const t = Math.max(TIER_MIN, Math.min(TIER_MAX, tier));
  return 1.0 + TIER_TIME_PER_LEVEL * (t - 1);
}

// Building level required to unlock tier N. Matches
// UnitTierScaling.RequiredBuildingLevel on the server.
export function requiredBuildingLevelForTier(tier: number): number {
  switch (tier) {
    case 1: return 1;
    case 2: return 4;
    case 3: return 7;
    case 4: return 10;
    case 5: return 13;
    case 6: return 16;
    case 7: return 20;
    case 8: return 23;
    case 9: return 26;
    default: return 30;
  }
}

// Highest tier the player can train in a building at this level.
export function maxAllowedTier(buildingLevel: number): number {
  for (let tier = TIER_MAX; tier >= TIER_MIN; tier--) {
    if (buildingLevel >= requiredBuildingLevelForTier(tier)) return tier;
  }
  return 0;
}

// Display name for the tier rank itself (e.g. "Ветеран" for tier 4).
// Per-unit display names live in UnitTierNames on the C# side and are
// served via the unit catalog or computed client-side when needed.
export function tierName(tier: number): string {
  switch (tier) {
    case 1: return "Новобранец";
    case 2: return "Ополченец";
    case 3: return "Воин";
    case 4: return "Ветеран";
    case 5: return "Элита";
    case 6: return "Гвардеец";
    case 7: return "Чемпион";
    case 8: return "Легенда";
    case 9: return "Герой";
    case 10: return "Мифический";
    default: return `T${tier}`;
  }
}
